import type { BoxProps } from '@hover/blueprint';
import {
  Box,
  Field,
  Form,
  IconButton,
  TextInput,
  motion,
  transition,
} from '@hover/blueprint';
import { iconClose, iconSearch } from '@hover/icons';
import React, { useRef, useState } from 'react';

import type { FiltersParams } from 'src/modules/properties/components/Filters/types';
import {
  useFilterParams,
  useSetFilterParams,
} from 'src/modules/properties/components/Filters/useFilterParams';
import {
  createJobFiltersFromUrlSearchParams,
  defaultJobFilters,
} from 'src/modules/properties/utils/filtersUtils';
import {
  TranslationNamespace,
  useCurrentPage,
  useTranslate,
} from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { textInput } from 'src/shared/telemetry';
import { getQueryParamsWithoutSearch } from 'src/shared/utils/url';

interface SearchInputProps extends BoxProps {
  isToggleable: boolean;
}

const MotionIconButton = motion(IconButton);

export const SearchInput = ({
  isToggleable = false,
  ...props
}: SearchInputProps) => {
  const filterParams = useFilterParams(createJobFiltersFromUrlSearchParams);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentPage = useCurrentPage();

  const isManageTeams = currentPage === Route.MANAGE_TEAMS.page;

  const searchTerm = filterParams?.search ?? '';

  const [isSearchActive, setIsSearchActive] = useState<boolean>(!!searchTerm);
  const [currentSearchValue, setCurrentSearchValue] =
    useState<string>(searchTerm);

  const t = useTranslate(TranslationNamespace.SHARED);

  const label = isManageTeams
    ? t('navigation.searchTeam')
    : t('navigation.searchProjects');

  const setFilterParams = useSetFilterParams<FiltersParams>(
    filterParams,
    defaultJobFilters,
  );

  const handleSearch = (searchValue: string) => {
    textInput({
      input_value: searchValue,
      input_label: 'search',
    });

    let pathWithSearchParams;

    if (searchTerm === searchValue) return;

    if (isManageTeams) {
      const existingParams = getQueryParamsWithoutSearch(location.search);
      pathWithSearchParams = `${
        Route.MANAGE_TEAMS.path
      }?search=${encodeURIComponent(searchValue)}${
        existingParams ? `&${existingParams}` : ''
      }`;
      window.location.href = pathWithSearchParams;
    } else {
      setFilterParams({
        ...filterParams,
        search: searchValue,
      });
    }
  };

  const clearSearch = () => {
    setIsSearchActive(false);
    setCurrentSearchValue('');
    handleSearch('');
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();

    if (currentSearchValue.length < 3) {
      // If the value was deleted through backspace, clear the search
      if (currentSearchValue === '') clearSearch();
      return;
    }

    handleSearch(currentSearchValue);
  };

  const showInput = !isToggleable || isSearchActive;

  return (
    <Box height="100%" position="relative" textAlign="right" {...props}>
      <Form
        onSubmit={handleSubmit}
        {...transition.easing250('opacity')}
        opacity={showInput ? 1 : 0}
        overflow="hidden"
        width="100%"
      >
        <Field
          label={label}
          name="navigationSearchInput"
          paddingBottom="0px !important"
        >
          <TextInput
            alignItems="center"
            defaultValue={searchTerm}
            elementAfter={
              currentSearchValue.length ? (
                <IconButton
                  icon={iconClose}
                  label="Clear search"
                  onClick={clearSearch}
                  size="small"
                  variant="ghost"
                />
              ) : (
                <></>
              )
            }
            iconBefore={iconSearch}
            // Always remount if the initialSearchTerm changes. This helps to clear state on reset.
            key={searchTerm}
            onBlur={() => {
              setIsSearchActive(!!currentSearchValue.length);
            }}
            onChange={e => {
              setCurrentSearchValue(e.currentTarget.value);
            }}
            placeholder=""
            ref={inputRef}
            value={currentSearchValue}
          />
        </Field>
      </Form>
      <MotionIconButton
        animate={showInput ? 'hide' : 'show'}
        exit={showInput ? 'show' : 'hide'}
        icon={iconSearch}
        initial={showInput ? 'show' : 'hide'}
        label={label}
        onClick={() => {
          if (!isSearchActive) {
            setIsSearchActive(true);
            inputRef.current?.focus();
          }
        }}
        position="absolute"
        right="0"
        top={200}
        variant={{ base: 'ghost', width350: 'glass' }}
        variants={{
          hide: { opacity: 0, display: 'none' },
          show: { opacity: 1, display: 'block' },
        }}
      />
    </Box>
  );
};
